/*
  assets/rad-video-shared.js

  Shared JS for both video players (product-page widget and reels
  feed). Deliberately small: only code that is byte-identical between
  the two lives here. Player-specific behaviour -- docking, feed
  building, hints, adoption -- stays in each player's own file.

  RadVideo.lockScroll() / RadVideo.unlockScroll():
  iOS-safe body scroll pinning while a fullscreen player is open.
  `overflow: hidden` on body is unreliable on iOS Safari (the page
  still rubber-band scrolls behind overlays), so the body is pinned
  with position:fixed at the current scroll offset and the position
  restored on unlock.

  RadVideo.hud.attach(scope, video) / .detach():
  Drives the progress bar and the play/pause button inside `scope`
  from `video`. Bound to one video at a time and re-pointed as the
  reels feed swipes, rather than instantiated per slide.

  WHAT USED TO BE HERE, and why it is gone

  This file also carried hdLoop -- a mechanism that kept a looping
  video sharp by warming the 1080p variant playlist in the background
  and switching the element onto it at the loop boundary -- plus a
  prewarm that filled the next slide's buffer from the same playlist,
  and a capability probe for whether the browser could decode HLS at
  all. Several hundred lines.

  All of it existed to work around one limitation: the native player
  cannot be told to change quality, so quality could only be changed
  by re-pointing the element, which reloads it and drops the picture.
  hdLoop's whole cleverness was hiding that reload at the loop seam,
  the only place it is invisible.

  Every video on the store now plays one fixed-quality MP4 from the
  first frame -- see snippets/rad-video-quality.liquid for why, and
  for the five failed attempts at keeping adaptive quality that led
  there. Nothing changes quality, so nothing needs hiding, and the
  machinery had no work left to do.

  Also gone from the theme: assets/rad-hls.js, which replaced hdLoop
  for a day and brought its own problem -- hls.js re-evaluates when a
  looping element seeks back to zero, which put a small stall at every
  lap. The browser's own player does not. That file is no longer
  referenced by anything; it remains only because the API used here
  cannot delete theme files.
*/
window.RadVideo = (function(){
  var scrollLockY = 0;

  /* ------------------------------------------------------------------
     hud -- progress bar and play/pause for whichever video is on screen
     ------------------------------------------------------------------

     Only one fullscreen player is ever open at a time, and the video
     behind it is not stable: the reels feed changes it on every swipe,
     and it borrows elements from the corner widget and the rail. So
     this tracks ONE video and gets re-pointed, rather than each slide
     carrying its own controls.

     Progress is driven by requestVideoFrameCallback where available:
     it fires once per rendered frame, so the bar moves as smoothly as
     the video itself, and it stops on its own when playback stops.
     `timeupdate` is the fallback, at roughly 4 updates a second.
  */
  var hud = { video: null, fill: null, btn: null, handle: 0 };

  function hudRender(){
    if (!hud.video || !hud.fill) { return; }
    var d = hud.video.duration;
    var p = (isFinite(d) && d > 0) ? (hud.video.currentTime / d) : 0;
    if (p < 0) { p = 0; }
    if (p > 1) { p = 1; }
    hud.fill.style.transform = 'scaleX(' + p.toFixed(4) + ')';
  }

  function hudSyncButton(){
    if (!hud.btn || !hud.video) { return; }
    var paused = hud.video.paused;
    var pauseIcon = hud.btn.querySelector('.rvw-icon-pause');
    var playIcon = hud.btn.querySelector('.rvw-icon-play');
    if (pauseIcon) { pauseIcon.style.display = paused ? 'none' : ''; }
    if (playIcon) { playIcon.style.display = paused ? '' : 'none'; }
    hud.btn.setAttribute('aria-label', paused ? 'Play video' : 'Pause video');
  }

  return {
    lockScroll: function(){
      scrollLockY = window.scrollY || window.pageYOffset || 0;
      document.body.style.position = 'fixed';
      document.body.style.top = -scrollLockY + 'px';
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.width = '100%';
    },
    unlockScroll: function(){
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.width = '';
      window.scrollTo(0, scrollLockY);
    },

    hud: {
      attach: function(scope, video){
        if (!scope || !video) { return; }
        this.detach();
        hud.video = video;
        hud.fill = scope.querySelector('[data-rvw-progress-fill]');
        hud.btn = scope.querySelector('[data-rvw-playpause]');

        if (hud.btn) {
          hud.onClick = function(e){
            /* Both players put a tap-to-pause handler on the whole
               surface; without this the tap would toggle twice and
               cancel itself out. */
            e.stopPropagation();
            if (hud.video.paused) { hud.video.play().catch(function(){}); }
            else { hud.video.pause(); }
          };
          hud.btn.addEventListener('click', hud.onClick);
        }

        hud.onState = function(){ hudSyncButton(); hudRender(); };
        video.addEventListener('play', hud.onState);
        video.addEventListener('pause', hud.onState);
        video.addEventListener('loadedmetadata', hud.onState);
        video.addEventListener('seeked', hudRender);

        if (typeof video.requestVideoFrameCallback === 'function') {
          hud.tick = function(){
            if (hud.video !== video) { return; }
            hudRender();
            hud.handle = video.requestVideoFrameCallback(hud.tick);
          };
          hud.handle = video.requestVideoFrameCallback(hud.tick);
        } else {
          hud.onTimeUpdate = function(){ hudRender(); };
          video.addEventListener('timeupdate', hud.onTimeUpdate);
        }

        hudSyncButton();
        hudRender();
      },

      detach: function(){
        var video = hud.video;
        if (video) {
          if (hud.handle && typeof video.cancelVideoFrameCallback === 'function') {
            try { video.cancelVideoFrameCallback(hud.handle); } catch(e) {}
          }
          if (hud.onTimeUpdate) { video.removeEventListener('timeupdate', hud.onTimeUpdate); }
          if (hud.onState) {
            video.removeEventListener('play', hud.onState);
            video.removeEventListener('pause', hud.onState);
            video.removeEventListener('loadedmetadata', hud.onState);
          }
          video.removeEventListener('seeked', hudRender);
        }
        if (hud.btn && hud.onClick) { hud.btn.removeEventListener('click', hud.onClick); }
        if (hud.fill) { hud.fill.style.transform = 'scaleX(0)'; }
        hud.video = null; hud.fill = null; hud.btn = null;
        hud.handle = 0; hud.tick = null; hud.onState = null;
        hud.onTimeUpdate = null; hud.onClick = null;
      }
    }
  };
})();

/*
  FILL vs LETTERBOX comparison switch.

  ?radfit=cover  -- fullscreen video fills the screen and crops
  ?radfit=contain -- fullscreen video shows the whole frame (default)

  The choice sticks for the browsing session so the whole site can be
  judged in one mode. This is a decision aid, not a feature: once a
  mode is picked, the losing branch and this block go away.
*/
(function(){
  try {
    var m = /[?&]radfit=(cover|contain)/.exec(location.search);
    if (m) { sessionStorage.setItem('radVideoFit', m[1]); }
    var mode = sessionStorage.getItem('radVideoFit');
    document.documentElement.classList.toggle('rvw-fit-cover', mode === 'cover');
  } catch(e) {}
})();
